import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DataService } from '../../services/data.service';
import {
  StaffMember, StaffStatus, StaffType, EmploymentType, StaffGender,
  STAFF_TYPES, EMPLOYMENT_TYPES, STAFF_STATUSES, STAFF_GENDERS,
  STAFF_DEPARTMENTS, STAFF_DESIGNATIONS, STAFF_BLOOD_GROUPS, STAFF_STATES,
  generateStaffMockData, generateStaffEmpId,
} from '../../common/staff.models';
import {
  job_opening, job_status, app_status,
  JOB_EMP_TYPE_OPTS, JOB_TYPE_LABELS, JOB_STATUS_OPTS, JOB_STATUS_LABELS, job_public_url,
} from './recruitment.models';
import { RecruitmentService } from './recruitment.service';

// ─────────────────────── types ────────────────────────────
export type leave_type        = 'casual'|'sick'|'earned'|'maternity'|'paternity'|'compensatory'|'unpaid';
export type leave_status      = 'pending'|'approved'|'rejected'|'cancelled';
export type payroll_status    = 'pending'|'processed'|'paid';
export type att_status        = 'present'|'absent'|'half_day'|'late'|'on_leave'|'holiday';
export type { job_emp_type, job_status, app_status, job_opening, job_application } from './recruitment.models';
export type hr_tab            = 'dashboard'|'employees'|'leaves'|'attendance'|'payroll'|'recruitment';
export type leaves_sub        = 'applications'|'balance';

export interface emp_qualification {
  degree: string; subject: string; institution: string;
  year_of_passing: number | null; percentage: number | null;
}
export interface emp_document {
  name: string; file_name: string; file: File | null;
}

export interface employee {
  id: number;
  emp_code: string;
  first_name: string;
  last_name: string;
  gender: StaffGender;
  dob: string;
  blood_group: string;
  mobile: string;
  email: string;
  address: string;
  city: string;
  state: string;
  pin_code: string;
  staff_type: StaffType;
  department: string;
  designation: string;
  employment: EmploymentType;
  doj: string;
  dol: string;
  experience: number | null;
  subjects: string[];
  qualifications: emp_qualification[];
  basic_salary: number | null;
  aadhaar_number: string;
  pan_number: string;
  uan_number: string;
  status: StaffStatus;
  profile_photo: string;
  documents: emp_document[];
  remarks: string;
  reg_date: string;
  is_active: boolean;
}

export interface leave_application {
  id: number; emp_code: string; emp_name: string; department: string;
  leave_type: leave_type; from_date: string; to_date: string; days: number;
  reason: string; status: leave_status; applied_date: string;
  approved_by: string; approved_at: string; remarks: string;
}
export interface payroll_record {
  id: number; emp_code: string; emp_name: string; department: string; designation: string;
  month: string; working_days: number; present_days: number;
  basic_salary: number; hra: number; da: number; special_allowance: number;
  gross_salary: number; pf_deduction: number; professional_tax: number;
  tds_deduction: number; other_deductions: number; net_salary: number;
  payment_mode: string; payment_date: any; status: payroll_status;
}
export interface att_record {
  id: number; emp_code: string; emp_name: string; department: string;
  date: string; check_in: string; check_out: string;
  status: att_status; working_hours: number; remarks: string;
}
export interface leave_balance {
  emp_code: string; emp_name: string; department: string;
  casual_total: number; casual_used: number;
  sick_total: number;   sick_used: number;
  earned_total: number; earned_used: number;
}

@Component({
  selector:    'app-hr-admin',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './hr-admin.html',
  styleUrl:    './hr-admin.scss',
})
export class HrAdminComponent {
  dataService = inject(DataService);
  loading = false;
  user: any = null;

  private rec    = inject(RecruitmentService);
  // private router = inject(Router);

  active_tab   = signal<hr_tab>('dashboard');
  leaves_sub   = signal<leaves_sub>('applications');
  toast_msg    = signal('');

  show_emp_modal    = signal(false);
  show_leave_modal  = signal(false);
  show_att_modal    = signal(false);
  show_job_modal    = signal(false);
  show_job_detail   = signal<job_opening | null>(null);
  editing_job_id    = signal<number | null>(null);
  show_payslip      = signal<payroll_record | null>(null);
  show_emp_detail   = signal<employee | null>(null);
  show_reject_modal = signal<number | null>(null);
  editing_id        = signal<number | null>(null);

  emp_form:    Partial<employee>          = {};
  subjects_text    = '';
  leave_form:  Partial<leave_application> = {};
  att_form:    Partial<att_record>        = {};
  job_form:    Partial<job_opening>       = {};
  emp_submitted    = false;
  leave_submitted  = false;
  att_submitted    = false;
  job_submitted    = false;
  reject_reason    = '';

  // Filters are signals so computed lists re-run when ngModel updates them
  emp_search      = signal('');
  emp_dept_f      = signal('');
  emp_staff_f     = signal<StaffType | ''>('');
  emp_status_f    = signal<StaffStatus | ''>('Active');
  emp_type_f      = signal<EmploymentType | ''>('');
  leave_search    = signal('');
  leave_status_f  = signal<leave_status | 'all'>('pending');
  leave_type_f    = signal<leave_type | ''>('');
  att_search      = signal('');
  att_date        = signal(this.today_str());
  att_dept_f      = signal('');
  att_status_f    = signal<att_status | ''>('');
  pay_search      = signal('');
  pay_month       = signal(this.current_month_str());
  pay_dept_f      = signal('');
  pay_status_f    = signal<payroll_status | ''>('');
  rec_search      = signal('');
  rec_status_f    = signal<job_status | ''>('open');
  selected_job    = signal<job_opening | null>(null);
  app_search      = signal('');

  private next_emp_id   = 100;
  private next_leave_id = 12;
  private next_att_id   = 20;

  employees       = signal<employee[]>([]);           //(this.build_employees());
  leave_apps      = signal<leave_application[]>([]);  //(this.build_leaves());
  payroll_recs    = signal<payroll_record[]>([]);     //(this.build_payroll());
  job_openings    = this.rec.jobs;
  job_apps        = this.rec.apps;
  att_records     = signal<att_record[]>([]);         //(this.build_attendance());
  leave_bals      = signal<leave_balance[]>([]);      //(this.build_leave_balances());

  readonly staff_type_opts   = STAFF_TYPES;
  readonly emp_type_opts     = EMPLOYMENT_TYPES;
  readonly emp_status_opts   = STAFF_STATUSES;
  readonly gender_opts       = STAFF_GENDERS;
  readonly dept_opts         = STAFF_DEPARTMENTS;
  readonly blood_group_opts  = STAFF_BLOOD_GROUPS;
  readonly state_opts        = STAFF_STATES;
  readonly leave_type_opts: leave_type[]     = ['casual','sick','earned','maternity','paternity','compensatory','unpaid'];
  readonly att_status_opts: att_status[]     = ['present','absent','late','half_day','on_leave','holiday'];
  readonly job_emp_type_opts = JOB_EMP_TYPE_OPTS;
  readonly job_type_labels   = JOB_TYPE_LABELS;
  readonly job_status_opts   = JOB_STATUS_OPTS;
  readonly job_status_labels = JOB_STATUS_LABELS;

  readonly dash_stats = computed(() => {
    const emps = this.employees().filter(e => e.status !== 'Resigned' && e.status !== 'Terminated');
    const today_att = this.att_records().filter(a => a.date === this.today_str());
    return {
      total:          emps.length,
      active:         emps.filter(e => e.status === 'Active').length,
      on_leave:       emps.filter(e => e.status === 'On Leave').length,
      teaching:       emps.filter(e => e.staff_type === 'Teaching').length,
      non_teaching:   emps.filter(e => e.staff_type === 'Non-Teaching').length,
      administrative: emps.filter(e => e.staff_type === 'Administrative').length,
      support:        emps.filter(e => e.staff_type === 'Support').length,
      present_today:  today_att.filter(a => ['present','late','half_day'].includes(a.status)).length,
      absent_today:   today_att.filter(a => a.status === 'absent').length,
      pending_leaves: this.leave_apps().filter(l => l.status === 'pending').length,
      open_positions: this.job_openings().filter(j => j.status === 'open').reduce((s,j) => s+(j.positions-j.filled),0),
      monthly_bill:   emps.filter(e => e.status === 'Active').reduce((s,e) => s+(e.basic_salary ?? 0), 0),
    };
  });

  readonly dept_stats = computed(() => {
    const counts: Record<string,number> = {};
    this.employees().filter(e => e.status === 'Active').forEach(e => {
      counts[e.department] = (counts[e.department] ?? 0) + 1;
    });
    return Object.entries(counts).map(([dept,count]) => ({dept,count})).sort((a,b) => b.count - a.count);
  });
  readonly max_dept_count = computed(() => Math.max(...this.dept_stats().map(d => d.count), 1));

  readonly staff_composition = computed(() => ([
    { type: 'Teaching' as StaffType,        label: 'Teaching',        color: '#059669', count: this.dash_stats().teaching },
    { type: 'Non-Teaching' as StaffType,    label: 'Non-Teaching',    color: '#d97706', count: this.dash_stats().non_teaching },
    { type: 'Administrative' as StaffType,  label: 'Administrative',  color: '#1a3a6b', count: this.dash_stats().administrative },
    { type: 'Support' as StaffType,         label: 'Support',         color: '#7c3aed', count: this.dash_stats().support },
  ]));

  readonly filtered_employees = computed(() => {
    const q      = this.emp_search().toLowerCase().trim();
    const dept   = this.emp_dept_f();
    const stype  = this.emp_staff_f();
    const status = this.emp_status_f();
    const etype  = this.emp_type_f();
    return this.employees().filter(e => {
      const hay = [e.first_name, e.last_name, e.emp_code, e.designation, e.department, e.email, e.mobile, e.staff_type]
        .join(' ').toLowerCase();
      return (!q || hay.includes(q))
        && (!dept   || e.department  === dept)
        && (!stype  || e.staff_type  === stype)
        && (!status || e.status      === status)
        && (!etype  || e.employment  === etype);
    }).sort((a,b) => a.emp_code.localeCompare(b.emp_code));
  });

  readonly filtered_leaves = computed(() => {
    const q      = this.leave_search().toLowerCase().trim();
    const status = this.leave_status_f();
    const ltype  = this.leave_type_f();
    return this.leave_apps().filter(l =>
      (!q || [l.emp_name, l.emp_code, l.department, l.reason].some(v => v.toLowerCase().includes(q))) &&
      (status === 'all' || l.status === status) &&
      (!ltype || l.leave_type === ltype)
    ).sort((a,b) => b.applied_date.localeCompare(a.applied_date));
  });

  readonly leave_counts = computed(() => {
    const la = this.leave_apps();
    return {
      pending:  la.filter(l => l.status === 'pending').length,
      approved: la.filter(l => l.status === 'approved').length,
      rejected: la.filter(l => l.status === 'rejected').length,
    };
  });

  readonly filtered_att = computed(() => {
    const q      = this.att_search().toLowerCase().trim();
    const date   = this.att_date();
    const dept   = this.att_dept_f();
    const status = this.att_status_f();
    const recs   = this.att_records().filter((a: any) => a.att_date === date);
    const by_emp = new Map(recs.map((a: any) => [a.emp_code, a]));
    const roster = this.employees()
      .filter(e => e.is_active || e.status === 'Active' || e.status === 'On Leave' || by_emp.has(e.emp_code))
      .map(e => by_emp.get(e.emp_code) ?? this.blank_att_row(e, date));
    return roster.filter((a: any) =>
      (!dept   || a.department === dept) &&
      (!status || (a.id > 0 && a.status === status)) &&
      (!q || [a.emp_name, a.emp_code, a.department].some(v => v.toLowerCase().includes(q)))
    );
    // .sort((a,b) => a?.emp_code.localeCompare(b?.emp_code));
  });

  readonly att_summary = computed(() => {
    const r = this.filtered_att().filter(a => a.id > 0);
    return {
      present:  r.filter(a => a.status === 'present').length,
      absent:   r.filter(a => a.status === 'absent').length,
      late:     r.filter(a => a.status === 'late').length,
      half_day: r.filter(a => a.status === 'half_day').length,
      on_leave: r.filter(a => a.status === 'on_leave').length,
      holiday:  r.filter(a => a.status === 'holiday').length,
    };
  });

  readonly filtered_payroll = computed(() => {
    const q      = this.pay_search().toLowerCase().trim();
    const month  = this.pay_month();
    const dept   = this.pay_dept_f();
    const status = this.pay_status_f();
    return this.payroll_recs().filter(p =>
      p.month === month &&
      (!dept   || p.department === dept) &&
      (!status || p.status     === status) &&
      (!q || [p.emp_name, p.emp_code, p.department, p.designation].some(v => v.toLowerCase().includes(q)))
    ).sort((a,b) => a.emp_code.localeCompare(b.emp_code));
  });

  readonly pay_summary = computed(() => {
    const p = this.filtered_payroll();
    return {
      gross:   p.reduce((s,r) => s + Number(r.gross_salary), 0),
      net:     p.reduce((s,r) => s + Number(r.net_salary), 0),
      pf:      p.reduce((s,r) => s + Number(r.pf_deduction), 0),
      tds:     p.reduce((s,r) => s + Number(r.tds_deduction), 0),
      paid:    p.filter(r => r.status === 'paid').length,
      pending: p.filter(r => r.status !== 'paid').length,
    };
  });

  readonly filtered_jobs = computed(() => {
    const q      = this.rec_search().toLowerCase().trim();
    const status = this.rec_status_f();
    return this.job_openings().filter(j =>
      (!status || j.status === status) &&
      (!q || [j.title, j.job_code, j.department, j.description].some(v => v.toLowerCase().includes(q)))
    ).sort((a,b) => b.posted_date.localeCompare(a.posted_date));
  });

  readonly apps_for_job = computed(() => {
    const job = this.selected_job();
    const q   = this.app_search().toLowerCase().trim();
    if (!job) return [];
    return this.job_apps()
      .filter(a => a.job_code === job.job_code && (!q || [a.applicant_name, a.email, a.phone].some(v => v.toLowerCase().includes(q))))
      .sort((a,b) => b.applied_date.localeCompare(a.applied_date));
  });

  readonly app_pipeline = computed(() => {
    const all = this.apps_for_job();
    return {
      applied:     all.filter(a => a.status === 'applied').length,
      shortlisted: all.filter(a => a.status === 'shortlisted').length,
      interviewed: all.filter(a => a.status === 'interviewed').length,
      selected:    all.filter(a => a.status === 'selected').length,
      rejected:    all.filter(a => a.status === 'rejected').length,
    };
  });

  // unique_depts: { code: string; name: string; }[] = this.dataService.departments;
  readonly unique_depts = computed(() =>
    [...new Set([...this.dataService.departments].filter(Boolean))].sort()
  );

  get emp_designations(): string[] {
    return STAFF_DESIGNATIONS[this.emp_form.staff_type ?? 'Teaching'] ?? [];
  }

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");
    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

    this.loading = true;
    await this.dataService.getAllRecords('staff').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        let emp: any = [];
        let temp: any = {};

        for(let i=0; i<res.length; i++) {
          temp = res[i];

          if(typeof temp.documents == 'string') {
            temp.documents = this.dataService.str2json(temp.documents);
          }
          if(typeof temp.qualifications == 'string') {
            temp.qualifications = this.dataService.str2json(temp.qualifications);
          }

          if(typeof temp.subjects == 'string') {
            if(temp.subjects == "{}") {
              temp.subjects = '';
            } else {
              temp.subjects = temp.subjects.replace('}', ']');
              temp.subjects = temp.subjects.replace('{', '[');
              temp.subjects = JSON.parse(temp.subjects);
            }
          }

          emp.push(temp);
        }

        this.employees.set(emp);

        this.leave_bals.set(this.build_leave_balances());
        // this.payroll_recs.set(this.build_payroll());
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('staff_att').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.att_records.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('staff_leaves').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.leave_apps.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('staff_payroll').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.payroll_recs.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('job_opening').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.rec.jobs.set(res);
        this.job_openings.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('job_apps').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.rec.apps.set(res);
        this.job_apps.set(res);
      }
    });

  }

  // ── employee CRUD ─────────────────────────────────────────
  open_emp_form(emp?: employee): void {
    this.emp_form = emp
      ? { ...emp, subjects: {...emp.subjects}, 
        qualifications: emp.qualifications.map(q => ({ ...q })), 
        documents: emp.documents.map(d => ({ ...d })) }
      : {
          ...this.blank_employee(),
          employment: 'Permanent',
          staff_type: 'Teaching',
          status: 'Active',
          is_active: true,
          doj: this.today_str(),
          emp_code: generateStaffEmpId('Teaching', this.next_emp_id, new Date().getFullYear()),
        };
    if(this.emp_form.subjects) {
      this.subjects_text = (this.emp_form.subjects).toString();
    } else {
      this.subjects_text = '';
    }
    this.editing_id.set(emp?.id ?? null);
    this.emp_submitted = false;
    this.show_emp_modal.set(true);
  }

  on_emp_status_change(): void {
    const status = this.emp_form.status;
    if (!status) return;
    this.emp_form.is_active = status !== 'Terminated' && status !== 'Resigned' && status !== 'Inactive';
  }

  on_staff_type_change(): void {
    const allowed = this.emp_designations;
    if (this.emp_form.designation && !allowed.includes(this.emp_form.designation)) {
      this.emp_form.designation = '';
    }
    if (!this.editing_id() && this.emp_form.staff_type) {
      this.emp_form.emp_code = generateStaffEmpId(this.emp_form.staff_type, this.next_emp_id, new Date().getFullYear());
    }
  }

  save_employee(): void {
    this.emp_submitted = true;
    if (!this.emp_form.first_name?.trim() || !this.emp_form.last_name?.trim()) return;
    const id = this.editing_id();
    const status = this.emp_form.status ?? 'Active';
    const is_active = this.emp_form.is_active ?? !['Terminated', 'Resigned', 'Inactive'].includes(status);
    const subjects = this.subjects_text.split(',').map(s => s.trim()).filter(Boolean);
    if (id) {
      this.employees.update(l => l.map(e => e.id === id
        ? { ...e, ...this.emp_form, id: e.id, emp_code: this.emp_form.emp_code || e.emp_code, status, is_active, subjects } as employee
        : e));

        this.employees()?.filter((obj: any) => {
          if(obj.id == id) {
            this.doUpdate('staff', obj);
          }
        });
    } else {
      const n = this.next_emp_id++;
      const staff_type = this.emp_form.staff_type ?? 'Teaching';
      this.employees.update(l => [...l, {
        ...this.blank_employee(),
        ...this.emp_form,
        id: n,
        emp_code: this.emp_form.emp_code || generateStaffEmpId(staff_type, n, new Date().getFullYear()),
        first_name: this.emp_form.first_name!.trim(),
        last_name: this.emp_form.last_name!.trim(),
        status,
        is_active,
        subjects,
        uan_number: this.emp_form.uan_number || this.make_uan(n),
        reg_date: new Date().toISOString(),
      }]);

      this.employees()?.filter((obj: any) => {
        if(obj.id == n) {
          this.doSave('staff', obj);
        }
      });

    }
    this.show_emp_modal.set(false);
    this.flash_toast('Employee saved.');
  }

  terminate_employee(id: number): void {
    if (!confirm('Mark this employee as terminated?')) return;
    this.employees.update(l => l.map(e => e.id === id ? { ...e, status: 'Terminated', is_active: false, dol: this.today_str() } : e));
    this.flash_toast('Employee terminated.');
  }

  view_employee(emp: employee): void { this.show_emp_detail.set(emp); }

  reset_emp_filters(): void {
    this.emp_search.set('');
    this.emp_dept_f.set('');
    this.emp_staff_f.set('');
    this.emp_status_f.set('Active');
    this.emp_type_f.set('');
  }

  reset_leave_filters(): void {
    this.leave_search.set('');
    this.leave_status_f.set('all');
    this.leave_type_f.set('');
  }

  reset_att_filters(): void {
    this.att_search.set('');
    this.att_date.set(this.today_str());
    this.att_dept_f.set('');
    this.att_status_f.set('');
  }

  // ── leave management ──────────────────────────────────────
  open_leave_form(): void {
    this.leave_form = { leave_type: 'casual', from_date: this.today_str(), to_date: this.today_str(), days: 1, reason: '' };
    this.leave_submitted = false;
    this.show_leave_modal.set(true);
  }

  calc_leave_days(): void {
    if (this.leave_form.from_date && this.leave_form.to_date) {
      const ms = new Date(this.leave_form.to_date).getTime() - new Date(this.leave_form.from_date).getTime();
      this.leave_form.days = Math.max(1, Math.floor(ms / 86400000) + 1);
    }
  }

  save_leave(): void {
    this.leave_submitted = true;
    if (!this.leave_form.emp_code || !this.leave_form.from_date || !this.leave_form.reason?.trim()) return;
    const emp = this.employees().find(e => e.emp_code === this.leave_form.emp_code);
    if (!emp) return;
    const n = this.next_leave_id++;
    this.leave_apps.update(l => [{
      id: n, emp_code: emp.emp_code, emp_name: this.get_full_name(emp),
      department: emp.department, leave_type: this.leave_form.leave_type ?? 'casual',
      from_date: this.leave_form.from_date!, to_date: this.leave_form.to_date ?? this.leave_form.from_date!,
      days: this.leave_form.days ?? 1, reason: this.leave_form.reason!,
      status: 'pending', applied_date: this.today_str(), approved_by: '', approved_at: '', remarks: '',
    }, ...l]);
    this.show_leave_modal.set(false);
    this.flash_toast('Leave application submitted.');
  }

  approve_leave(id: number): void {
    this.leave_apps.update(l => l.map(a => a.id === id ? { ...a, status: 'approved', approved_by: 'HR Admin', approved_at: this.today_str() } : a));
    this.flash_toast('Leave approved.');
  }

  open_reject(id: number): void { this.show_reject_modal.set(id); this.reject_reason = ''; }

  confirm_reject(): void {
    const id = this.show_reject_modal();
    if (!id) return;
    this.leave_apps.update(l => l.map(a => a.id === id ? { ...a, status: 'rejected', remarks: this.reject_reason, approved_by: 'HR Admin', approved_at: this.today_str() } : a));
    this.show_reject_modal.set(null);
    this.flash_toast('Leave rejected.');
  }

  // ── attendance ────────────────────────────────────────────
  open_att_form(row?: att_record): void {
    if (row && row.emp_code) {
      this.att_form = { ...row, date: row.date || this.att_date() };
      if (!row.check_in && this.att_status_needs_times(this.att_form.status ?? 'present')) {
        const def = this.default_att_times(this.att_form.status ?? 'present');
        this.att_form.check_in = def.check_in;
        this.att_form.check_out = this.att_form.check_out || def.check_out;
      }
    } else {
      this.att_form = {
        emp_code: '', emp_name: '', department: '',
        date: this.att_date(), status: 'present',
        check_in: '09:00', check_out: '17:00',
        working_hours: 8, remarks: '',
      };
    }
    this.on_att_time_change();
    this.att_submitted = false;
    this.show_att_modal.set(true);
  }

  on_att_emp_change(): void {
    const emp = this.employees().find(e => e.emp_code === this.att_form.emp_code);
    if (!emp) return;
    this.att_form.emp_name = this.get_full_name(emp);
    this.att_form.department = emp.department;
    const existing = this.att_records().find(a => a.emp_code === emp.emp_code && a.date === (this.att_form.date || this.att_date()));
    if (existing) {
      this.att_form = { ...existing };
      this.on_att_time_change();
    }
  }

  on_att_status_change(): void {
    const st = this.att_form.status;
    if (!st) return;
    if (this.att_status_needs_times(st)) {
      const def = this.default_att_times(st);
      if (!this.att_form.check_in)  this.att_form.check_in  = def.check_in;
      if (!this.att_form.check_out) this.att_form.check_out = def.check_out;
    } else {
      this.att_form.check_in = '';
      this.att_form.check_out = '';
    }
    this.on_att_time_change();
  }

  on_att_time_change(): void {
    const st = this.att_form.status ?? 'present';
    if (!this.att_status_needs_times(st)) {
      this.att_form.working_hours = 0;
      return;
    }
    this.att_form.working_hours = this.calc_working_hours(this.att_form.check_in ?? '', this.att_form.check_out ?? '');
  }

  save_attendance(): void {
    this.att_submitted = true;
    if (!this.att_form.emp_code || !this.att_form.date) return;
    const st = this.att_form.status ?? 'present';
    if (this.att_status_needs_times(st) && !this.att_form.check_in) return;
    this.upsert_att(this.att_form.emp_code, {
      date: this.att_form.date,
      status: st,
      check_in: this.att_form.check_in ?? '',
      check_out: this.att_form.check_out ?? '',
      working_hours: this.calc_working_hours(this.att_form.check_in ?? '', this.att_form.check_out ?? ''),
      remarks: this.att_form.remarks ?? '',
      emp_name: this.att_form.emp_name,
      department: this.att_form.department,
    });
    this.att_date.set(this.att_form.date ?? this.today_str());
    this.show_att_modal.set(false);
    this.flash_toast('Attendance marked.');
  }

  mark_att(emp_code: string, new_status: att_status): void {
    if (!emp_code || !new_status) return;
    const date = this.att_date();
    const existing = this.att_records().find(a => a.emp_code === emp_code && a.date === date);
    let check_in = existing?.check_in ?? '';
    let check_out = existing?.check_out ?? '';
    if (this.att_status_needs_times(new_status)) {
      const def = this.default_att_times(new_status);
      if (!check_in)  check_in  = def.check_in;
      if (!check_out) check_out = def.check_out;
    } else {
      check_in = '';
      check_out = '';
    }
    this.upsert_att(emp_code, {
      date, status: new_status, check_in, check_out,
      working_hours: this.calc_working_hours(check_in, check_out),
    });
    this.flash_toast('Attendance updated.');
  }

  set_att_check_in(row: att_record, time: string): void {
    const check_out = row.check_out ?? '';
    const hours = this.calc_working_hours(time, check_out);
    const status = this.att_status_needs_times(row.status) || !row.id
      ? this.status_from_times(time, check_out, hours)
      : row.status;
    this.upsert_att(row.emp_code, {
      date: row.date || this.att_date(),
      check_in: time, check_out, working_hours: hours, status,
    });
  }

  set_att_check_out(row: att_record, time: string): void {
    const check_in = row.check_in ?? '';
    const hours = this.calc_working_hours(check_in, time);
    const status = this.att_status_needs_times(row.status) || !row.id
      ? this.status_from_times(check_in, time, hours)
      : row.status;
    this.upsert_att(row.emp_code, {
      date: row.date || this.att_date(),
      check_in, check_out: time, working_hours: hours, status,
    });
  }

  att_times_disabled(status: att_status): boolean {
    return !this.att_status_needs_times(status);
  }

  fmt_hours(n: number | null | undefined): string {
    if (n == null || n <= 0) return '—';
    if(typeof n === 'string') n = parseFloat(n);

    return n.toFixed(1) + ' h';
  }

  calc_working_hours(check_in: string, check_out: string): number {
    if (!check_in || !check_out) return 0;
    const to_mins = (t: string): number => {
      const parts = t.split(':').map(Number);
      return (parts[0] || 0) * 60 + (parts[1] || 0);
    };
    let mins = to_mins(check_out) - to_mins(check_in);
    if (mins < 0) mins += 24 * 60;
    return Math.round((mins / 60) * 10) / 10;
  }

  private att_status_needs_times(st: att_status): boolean {
    return st === 'present' || st === 'late' || st === 'half_day';
  }

  private default_att_times(st: att_status): { check_in: string; check_out: string } {
    if (st === 'late')     return { check_in: '10:15', check_out: '17:00' };
    if (st === 'half_day') return { check_in: '09:00', check_out: '13:00' };
    return { check_in: '09:00', check_out: '17:00' };
  }

  private status_from_times(check_in: string, check_out: string, hours: number): att_status {
    if (!check_in && !check_out) return 'absent';
    if (hours > 0 && hours < 4) return 'half_day';
    if (check_in && check_in > '09:00') return 'late';
    return 'present';
  }

  private blank_att_row(e: employee, date: string): att_record {
    return {
      id: 0, emp_code: e.emp_code, emp_name: this.get_full_name(e),
      department: e.department, date,
      check_in: '', check_out: '', status: 'present',
      working_hours: 0, remarks: '',
    };
  }

  private upsert_att(emp_code: string, patch: Partial<att_record>): void {
    const date = patch.date ?? this.att_date();
    const existing = this.att_records().find(a => a.emp_code === emp_code && a.date === date);

    if (existing) {
      this.att_records.update(l => l.map(a => a.id === existing.id ? { ...a, ...patch, id: existing.id, emp_code, date } : a));

      this.att_records()?.filter((obj: any) => {
        if(obj.id == existing.id) {
          this.doUpdate('staff_att', obj);
        }
      });

      return;
    }

    const emp = this.employees().find(e => e.emp_code === emp_code);
    this.att_records.update(l => [...l, {
      id: this.next_att_id++,
      emp_code,
      emp_name: patch.emp_name || (emp ? this.get_full_name(emp) : emp_code),
      department: patch.department || emp?.department || '',
      date,
      check_in: patch.check_in ?? '',
      check_out: patch.check_out ?? '',
      status: patch.status ?? 'present',
      working_hours: patch.working_hours ?? 0,
      remarks: patch.remarks ?? '',
    }]);

    this.doSave('staff_att', {
      emp_code: emp?.emp_code,
      emp_name: patch.emp_name || (emp ? this.get_full_name(emp) : emp_code),
      department: patch.department || emp?.department || '',
      att_date: patch.date,
      check_in: patch.check_in ?? '',
      check_out: patch.check_out ?? '',
      status: patch.status ?? 'present',
      working_hours: patch.working_hours ?? 0,
      remarks: patch.remarks ?? '',
    });
  }

  // ── payroll ───────────────────────────────────────────────
  generate_payroll(): void {
    const month = this.pay_month();
    const existing_ids = new Set(this.payroll_recs().filter(p => p.month === month).map(p => p.emp_code));
    const active_emps  = this.employees().filter(e => e.status === 'Active' && !existing_ids.has(e.emp_code));
    if (!active_emps.length) { this.flash_toast('Payroll already generated for this month.'); return; }
    const new_recs = active_emps.map((e, i) => this.make_payslip(e, month, false, 200 + i));
    this.payroll_recs.update(l => [...l, ...new_recs]);

    let new_recz = structuredClone(new_recs);
    new_recz = new_recz.filter((obj: any) => {
      delete obj.id;
      return obj;
    });

    this.doSave('gen_payroll', new_recz);

    this.flash_toast(`Payroll generated for ${new_recs.length} employees.`);
  }

  mark_paid(id: number): void {
    this.payroll_recs.update(l => l.map(p => p.id === id ? { ...p, status: 'paid', payment_date: this.today_str() } : p));
    this.payroll_recs()?.filter((obj: any) => {
      if(obj.id === id) {
        this.doUpdate('staff_payroll', obj);
      }
    });

    this.flash_toast('Marked as paid.');
  }

  mark_all_paid(): void {
    const month = this.pay_month();

    this.payroll_recs.update(l => l.map(p => p.month === month && p.status !== 'paid' ? { ...p, status: 'paid', payment_date: this.today_str() } : p));
    this.doSave('bulk_payroll', this.payroll_recs());
    this.flash_toast('All payroll marked as paid.');
  }

  open_payslip(p: payroll_record): void { this.show_payslip.set(p); }

  // ── recruitment ───────────────────────────────────────────
  open_job_form(job?: job_opening, ev?: Event): void {
    ev?.stopPropagation();
    this.show_job_detail.set(null);
    if (job) {
      this.job_form = { ...job };
      this.editing_job_id.set(job.id);
    } else {
      this.job_form = {
        emp_type: 'full_time', positions: 1, filled: 0, status: 'open',
        posted_date: this.today_str(), description: '', requirements: '',
      };
      this.editing_job_id.set(null);
    }
    this.job_submitted = false;
    this.show_job_modal.set(true);
  }

  // save_job(): void {
  //   this.job_submitted = true;
  //   if (!this.job_form.title?.trim() || !this.job_form.department?.trim()) return;

  //   const job = this.rec.add_job({
  //     title: this.job_form.title!.trim(),
  //     department: this.job_form.department!,
  //     emp_type: this.job_form.emp_type ?? 'full_time',
  //     positions: this.job_form.positions ?? 1,
  //     description: this.job_form.description ?? '',
  //     requirements: this.job_form.requirements ?? '',
  //     closing_date: this.job_form.closing_date ?? '',
  //     status: this.job_form.status ?? 'open',
  //   });

  //   this.doSave('job_opening', job);

  //   this.show_job_modal.set(false);
  //   this.flash_toast('Job posting created.');
  // }
  save_job(): void {
    this.job_submitted = true;

    if (!this.job_form.title?.trim() || !this.job_form.department?.trim()) return;

    const payload = {
      title: this.job_form.title!.trim(),
      department: this.job_form.department!,
      emp_type: this.job_form.emp_type ?? 'full_time',
      positions: Math.max(1, this.job_form.positions ?? 1),
      description: this.job_form.description ?? '',
      requirements: this.job_form.requirements ?? '',
      closing_date: this.job_form.closing_date ?? '',
      status: this.job_form.status ?? 'open',
    };

    const id = this.editing_job_id();
    if (id) {
      const updated = this.rec.update_job(id, { ...payload, filled: this.job_form.filled ?? 0 });
      this.doUpdate('job_opening', updated);
      this.sync_job_refs(updated);
      this.flash_toast('Job posting updated.');
    } else {
      const created = this.rec.add_job(payload);
      this.doUpdate('job_opening', created);
      this.selected_job.set(created);
      this.flash_toast('Job posting created.');
    }
    this.show_job_modal.set(false);
  }

  change_job_status(job: job_opening, status: job_status, ev?: Event): void {
    ev?.stopPropagation();
    if (!status || status === job.status) return;
    const updated = this.rec.set_job_status(job.id, status);
    this.sync_job_refs(updated);
    this.flash_toast(`Status set to ${this.job_status_labels[status]}.`);
  }

  delete_job(job: job_opening, ev?: Event): void {
    ev?.stopPropagation();
    if (!confirm(`Delete job posting ${job.job_code} – ${job.title}? Applications for this opening will also be removed.`)) return;

    this.doDelete('job_opening', job.id);

    this.rec.delete_job(job.id);
    if (this.selected_job()?.id === job.id) this.selected_job.set(null);
    if (this.show_job_detail()?.id === job.id) this.show_job_detail.set(null);
    this.show_job_modal.set(false);
    this.flash_toast('Job posting deleted.');
  }

  private sync_job_refs(job?: job_opening): void {
    if (!job) return;
    if (this.selected_job()?.id === job.id) this.selected_job.set(job);
    if (this.show_job_detail()?.id === job.id) this.show_job_detail.set(job);
  }

  view_job(job: job_opening, ev?: Event): void {
    ev?.stopPropagation();
    this.selected_job.set(job);
    this.show_job_detail.set(job);
  }

  job_share_url(job: job_opening): string {
    return job_public_url(job.job_code);
  }

  copy_job_link(job: job_opening, ev?: Event): void {
    ev?.stopPropagation();
    const url = this.job_share_url(job);
    const done = (): void => this.flash_toast('Job link copied. Share it on career or social sites.');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => this.fallback_copy(url, done));
    } else {
      this.fallback_copy(url, done);
    }
  }

  // open_public_job(job: job_opening, ev?: Event): void {
  //   ev?.stopPropagation();
  //   this.show_job_detail.set(null);
  //   void this.router.navigate(['/careers', job.job_code]);
  // }

  social_share_url(job: job_opening, network: 'whatsapp' | 'linkedin' | 'x' | 'email'): string {
    const url = this.job_share_url(job);
    const text = `${job.title} – ${job.department} at EduSmart International School`;
    if (network === 'whatsapp') return `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`;
    if (network === 'linkedin') return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    if (network === 'x')        return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    return `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent('View this job posting:\n' + url)}`;
  }

  shortlist_app(id: number): void { this.rec.set_app_status(id, 'shortlisted'); this.flash_toast('Shortlisted.'); }
  interview_app(id: number): void { this.rec.set_app_status(id, 'interviewed'); this.flash_toast('Moved to interview.'); }
  select_app(id: number): void    { this.rec.set_app_status(id, 'selected'); this.flash_toast('Candidate selected!'); }
  reject_app(id: number): void    { this.rec.set_app_status(id, 'rejected'); this.flash_toast('Application rejected.'); }

  private fallback_copy(text: string, done: () => void): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); done(); }
    catch { this.flash_toast(text); }
    finally { document.body.removeChild(ta); }
  }

  // ── helpers ───────────────────────────────────────────────
  get_full_name(e: Pick<employee, 'first_name' | 'last_name'>): string {
    return [e.first_name, e.last_name].filter(Boolean).join(' ');
  }
  get_initials(name: string): string    { return name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase(); }
  calc_yoe(doj: string): string {
    if (!doj) return '—';
    const y = Math.floor((Date.now() - new Date(doj).getTime()) / 31557600000);
    return y < 1 ? '<1 yr' : `${y} yr${y !== 1 ? 's' : ''}`;
  }
  calc_age(dob: string): string {
    if (!dob) return '—';
    return `${Math.floor((Date.now() - new Date(dob).getTime()) / 31557600000)} yrs`;
  }

  today_str(): string               { return new Date().toISOString().substring(0, 10); }
  current_month_str(): string       { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
  iso_date(offset: number): string  { const d = new Date(); d.setDate(d.getDate() - offset); return d.toISOString().substring(0, 10); }
  iso_month(offset: number): string { const d = new Date(); d.setMonth(d.getMonth() - offset); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }

  fmt_date(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  fmt_curr(n: number | null | undefined): string {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n ?? 0);
  }

  fmt_short(n: any): string {
    // Convert the input to a reliable number first
    const num = Number(n);

    // Handle case where conversion fails (e.g., if input is completely invalid text)
    if (isNaN(num)) return '₹0';

    if (num >= 100000) return '₹' + (num / 100000).toFixed(2) + 'L';
    if (num >= 1000)   return '₹' + (num / 1000).toFixed(0) + 'K';
    return '₹' + num;
  }

  dept_bar_w(count: number): string { return (count / this.max_dept_count() * 100) + '%'; }

  has_emp_errors(): boolean {
    return this.emp_submitted && (!this.emp_form.first_name?.trim() || !this.emp_form.last_name?.trim());
  }

  get_emp_st_class(s: StaffStatus): string {
    return { Active: 's-active', Inactive: 's-inactive', 'On Leave': 's-onleave', Resigned: 's-resigned', Terminated: 's-terminated' }[s] ?? '';
  }
  get_leave_st_class(s: leave_status): string {
    return { pending: 's-pending', approved: 's-approved', rejected: 's-rejected', cancelled: 's-cancelled' }[s] ?? '';
  }
  get_att_st_class(s: att_status): string {
    return { present: 's-present', absent: 's-absent', late: 's-late', half_day: 's-half', on_leave: 's-onleave', holiday: 's-holiday' }[s] ?? '';
  }
  get_pay_st_class(s: payroll_status): string {
    return { pending: 's-pending', processed: 's-processed', paid: 's-paid-p' }[s] ?? '';
  }
  get_app_st_class(s: app_status): string {
    return { applied: 's-applied', shortlisted: 's-shortlisted', interviewed: 's-interviewed', selected: 's-selected', rejected: 's-rejected' }[s] ?? '';
  }
  get_job_st_class(s: job_status): string {
    return { open: 's-jobopen', closed: 's-jobclosed', on_hold: 's-jobhold' }[s] ?? '';
  }
  get_st_color(t: StaffType): string {
    return { Teaching: '#059669', 'Non-Teaching': '#d97706', Administrative: '#1a3a6b', Support: '#7c3aed' }[t] ?? '#6b7280';
  }
  get_st_bg(t: StaffType): string {
    return { Teaching: '#dcfce7', 'Non-Teaching': '#fef3c7', Administrative: '#dbeafe', Support: '#ede9fe' }[t] ?? '#f3f4f6';
  }
  get_leave_type_icon(t: leave_type): string {
    return {
      casual: 'bi-calendar-event', sick: 'bi-heart-pulse-fill', earned: 'bi-calendar-check',
      maternity: 'bi-person-heart', paternity: 'bi-person-plus', compensatory: 'bi-arrow-repeat', unpaid: 'bi-slash-circle',
    }[t] ?? 'bi-calendar';
  }
  get_att_icon(s: att_status): string {
    return {
      present: 'bi-check-circle-fill', absent: 'bi-x-circle-fill', late: 'bi-clock-fill',
      half_day: 'bi-circle-half', on_leave: 'bi-calendar-x-fill', holiday: 'bi-flag-fill',
    }[s] ?? 'bi-circle';
  }

  print_payslip(): void { window.print(); }
  private flash_toast(m: string): void { this.toast_msg.set(m); setTimeout(() => this.toast_msg.set(''), 3000); }

  private make_payslip(e: employee, month: string, paid: boolean, pid: number): payroll_record {
    const gross   = e.basic_salary ?? 0;
    const basic   = Math.round(gross * 0.5);
    const hra     = Math.round(basic * 0.4);
    const da      = Math.round(basic * 0.15);
    const special = gross - basic - hra - da;
    const pf      = Math.round(basic * 0.12);
    const pt      = 200;
    const tds     = gross > 50000 ? Math.round((gross - 50000) * 0.1) : 0;
    return {
      id: pid, emp_code: e.emp_code, emp_name: this.get_full_name(e),
      department: e.department, designation: e.designation, month,
      working_days: 26, present_days: 26, basic_salary: basic, hra, da, special_allowance: special,
      gross_salary: gross, pf_deduction: pf, professional_tax: pt, tds_deduction: tds,
      other_deductions: 0, net_salary: gross - pf - pt - tds,
      payment_mode: 'bank_transfer', payment_date: paid ? this.iso_date(5) : this.today_str() || null,
      status: paid ? 'paid' : 'pending',
    };
  }

  private make_uan(id: number): string {
    return String(100123456700 + id);
  }

  private blank_employee(): employee {
    return {
      id: 0, emp_code: '',
      first_name: '', last_name: '', gender: 'Male', dob: '', blood_group: '',
      mobile: '', email: '', address: '', city: '', state: '', pin_code: '',
      staff_type: 'Teaching', department: '', designation: '', employment: 'Permanent',
      doj: '', dol: '', experience: null, subjects: [],
      qualifications: [{ degree: '', subject: '', institution: '', year_of_passing: null, percentage: null }],
      basic_salary: null, aadhaar_number: '', pan_number: '', uan_number: '',
      status: 'Active', profile_photo: '', documents: [], remarks: '',
      reg_date: '', is_active: true,
    };
  }

  /*
  private staff_to_employee(s: StaffMember): employee {
    return {
      id: s.id,
      emp_code: s.employeeId,
      first_name: s.firstName,
      last_name: s.lastName,
      gender: s.gender,
      dob: s.dateOfBirth,
      blood_group: s.bloodGroup,
      mobile: s.mobile,
      email: s.email,
      address: s.address,
      city: s.city,
      state: s.state,
      pin_code: s.pinCode,
      staff_type: s.staffType,
      department: s.department,
      designation: s.designation,
      employment: s.employmentType,
      doj: s.dateOfJoining,
      dol: s.dateOfLeaving,
      experience: s.experience,
      subjects: s.subjects,
      qualifications: s.qualifications.map(q => ({
        degree: q.degree, subject: q.subject, institution: q.institution,
        year_of_passing: q.yearOfPassing, percentage: q.percentage,
      })),
      basic_salary: s.basicSalary,
      aadhaar_number: s.aadhaarNumber,
      pan_number: s.panNumber,
      uan_number: this.make_uan(s.id),
      status: s.status,
      profile_photo: s.profilePhoto,
      documents: s.documents.map(d => ({ name: d.name, file_name: d.fileName, file: d.file })),
      remarks: s.remarks,
      reg_date: s.createdAt,
      is_active: s.status !== 'Terminated' && s.status !== 'Resigned' && s.status !== 'Inactive',
    };
  }

  private build_employees(): employee[] {
    return generateStaffMockData().map(s => this.staff_to_employee(s));
  }

  // ─────────────────────── seed data ───────────────────────
  private build_leaves(): leave_application[] {
    const emps = this.build_employees();
    const pick = (i: number) => emps[i];
    const la = (id: number, e: employee, lt: leave_type, fd: string, td: string, days: number, reason: string, st: leave_status, ab = '', ar = '', rem = ''): leave_application => ({
      id, emp_code: e.emp_code, emp_name: this.get_full_name(e), department: e.department,
      leave_type: lt, from_date: fd, to_date: td, days, reason, status: st,
      applied_date: this.iso_date(days + 2), approved_by: ab, approved_at: ar, remarks: rem,
    });
    return [
      la(1,  pick(6),  'casual',       this.iso_date(0),  this.iso_date(0),  1, 'Personal work',            'pending'),
      la(2,  pick(11), 'sick',         this.iso_date(1),  this.iso_date(2),  2, 'Fever and cold',           'pending'),
      la(3,  pick(2),  'casual',       this.iso_date(3),  this.iso_date(4),  2, 'Family function',          'approved', 'HR Admin', this.iso_date(2)),
      la(4,  pick(0),  'earned',       this.iso_date(5),  this.iso_date(9),  5, 'Annual vacation',          'approved', 'HR Admin', this.iso_date(4)),
      la(5,  pick(1),  'sick',         this.iso_date(7),  this.iso_date(8),  2, 'Doctor appointment',       'approved', 'HR Admin', this.iso_date(6)),
      la(6,  pick(5),  'compensatory', this.iso_date(2),  this.iso_date(2),  1, 'Worked on Sunday',         'pending'),
      la(7,  pick(16), 'casual',       this.iso_date(10), this.iso_date(11), 2, 'Marriage in family',       'approved', 'HR Admin', this.iso_date(9)),
      la(8,  pick(12), 'sick',         this.iso_date(14), this.iso_date(15), 2, 'Viral infection',          'rejected', 'HR Admin', this.iso_date(13), 'Insufficient medical certificate'),
      la(9,  pick(10), 'casual',       this.iso_date(0),  this.iso_date(1),  2, 'Personal emergency',       'pending'),
      la(10, pick(8),  'earned',       this.iso_date(20), this.iso_date(24), 5, 'Planned vacation',         'approved', 'HR Admin', this.iso_date(19)),
      la(11, pick(12), 'casual',       this.iso_date(1),  this.iso_date(1),  1, 'Coach training program',   'approved', 'HR Admin', this.iso_date(0)),
    ];
  }

  private build_payroll(): payroll_record[] {
    const month = this.current_month_str();
    const prev  = this.iso_month(1);
    const emps  = this.employees().filter(e => e.status === 'Active');
    return [
      ...emps.map((e, i) => this.make_payslip(e, month, false, 10 + i)),
      ...emps.map((e, i) => this.make_payslip(e, prev,  true,  100 + i)),
    ];
  }

  private build_attendance(): att_record[] {
    const today = this.today_str();
    const emps  = this.employees().filter(e => e.status === 'Active' || e.status === 'On Leave');
    const st_map: Record<string, att_status> = {};
    emps.forEach(e => {
      if (e.status === 'On Leave') st_map[e.emp_code] = 'on_leave';
    });
    const late   = emps.find(e => e.staff_type === 'Support');
    const absent = emps.find(e => e.staff_type === 'Non-Teaching');
    if (late)   st_map[late.emp_code]   = st_map[late.emp_code]   ?? 'late';
    if (absent) st_map[absent.emp_code] = st_map[absent.emp_code] ?? 'absent';
    return emps.map((e, i) => {
      const st: att_status = st_map[e.emp_code] ?? 'present';
      const ci  = st === 'present' ? '08:45' : st === 'late' ? '10:20' : '';
      const co  = (st === 'present' || st === 'late') ? '17:00' : '';
      const hrs = st === 'present' ? 8.5 : st === 'late' ? 6.7 : 0;
      return {
        id: i + 1, emp_code: e.emp_code, emp_name: this.get_full_name(e),
        department: e.department, date: today, check_in: ci, check_out: co,
        status: st, working_hours: hrs, remarks: '',
      };
    });
  }
  */

  private build_leave_balances(): leave_balance[] {
    return this.employees().map((e, i) => ({
      emp_code: e.emp_code, emp_name: this.get_full_name(e), department: e.department,
      casual_total: 12, casual_used: (i % 4),
      sick_total: 12,   sick_used:   (i % 3),
      earned_total: 15, earned_used: (i % 6),
    }));
  }

  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}


}
